import { useMemo } from 'react'
import { useDataRead, DataHookOptions, useDataList } from '@hooks/data'
import { getDateFromNow } from '@utils/date-time'

import { eventApi } from './api'
import { venueApi } from '@modules/venue/data'
import { ListParams } from 'types/api'
import { VenueEvent } from 'types/model'
import { InfiniteDataHookOptions, useDataInfinite } from '@hooks/data-infinite'

function getEventPath(append?: number | string) {
  return append ? eventApi.pathPrefix.concat(`${append}`) : eventApi.pathPrefix
}

function getVenuePath(append?: number | string) {
  return append ? venueApi.pathPrefix.concat(`${append}`) : venueApi.pathPrefix
}

export function useEvent(event?: string | null, options?: DataHookOptions<VenueEvent>) {
  const path = event ? getEventPath(`/${event}`) : null
  return useDataRead<VenueEvent>(eventApi, path, options)
}

export function useVenueEvent(
  venue: string | null,
  event: string | null,
  options?: DataHookOptions<VenueEvent>
) {
  const path = venue && event ? getVenuePath(`/${venue}/events/${event}`) : null
  return useDataRead<VenueEvent>(venueApi, path, options)
}

export function useVenueDayEvent(
  venue: string | null,
  date: string = getDateFromNow().toISOString(),
  options?: DataHookOptions<VenueEvent[]>
) {
  const params = useMemo<ListParams<VenueEvent>>(
    () => ({
      limit: 1,
      skip: 0,
      sort: 'dateStart',
      date,
      dateStart: date,
    }),
    [date]
  )
  const { data, ...swrResponse } = useVenueEvents(venue, { ...options, params })

  return {
    ...swrResponse,
    data: data?.[0],
  }
}

export function useVenueEvents(venue: string | null, options?: DataHookOptions<VenueEvent[]>) {
  const path = venue ? getVenuePath(`/${venue}/events`) : null
  return useDataList<VenueEvent>(venueApi, path, options)
}

export function useInfiniteVenueEvents(
  venue: string | null,
  options: InfiniteDataHookOptions<VenueEvent[]>
) {
  const path = venue ? getVenuePath(`/${venue}/events`) : null
  return useDataInfinite<VenueEvent>(venueApi, path, options)
}
